import { createApp } from "vue"
import App from "./App.vue"
import router from "./router"
import axios from "axios";
import store from "./store";

import "./app.scss";
import "bootstrap/dist/js/bootstrap.min.js";

import authHeader from "./services/auth-header";
import authService from "./services/auth-service";
import stripeService from "./services/stripe-subscription-service";

//Kendo
import "@progress/kendo-ui/js/kendo.validator";
import { Grid } from "@progress/kendo-vue-grid";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { Window, Dialog } from "@progress/kendo-vue-dialogs";
import { Field, Form } from "@progress/kendo-vue-form";
import { Error, Hint, Label } from "@progress/kendo-vue-labels";
import { Input, MaskedTextBox, NumericTextBox, Checkbox, RadioButton, TextArea } from "@progress/kendo-vue-inputs";
// import { DateInput } from "@progress/kendo-vue-dateinputs";
import { Button, ButtonGroup, DropDownButton } from "@progress/kendo-vue-buttons";
import { Card, CardHeader, CardBody, CardTitle, CardSubtitle, CardImage, CardActions } from "@progress/kendo-vue-layout";
import { Upload } from "@progress/kendo-vue-upload";
import { Expand } from "@progress/kendo-vue-animation";
import { PanelBar } from "@progress/kendo-layout-vue-wrapper";
import { Notification, Tooltip } from "@progress/kendo-popups-vue-wrapper";
import { Switch } from "@progress/kendo-inputs-vue-wrapper";
import { Validator, ValidatorInstaller } from "@progress/kendo-validator-vue-wrapper";

//Axios
import $http from "axios";
import DialogHeader from "@/components/DialogHeader.vue";
import Version from "@/components/Version.vue";
import ResultColumnWithArrow from "@/components/table/ResultColumnWithArrow.vue";
import InputInGrid from "@/components/table/InputInGrid.vue";
import TextInputInGrid from "@/components/table/TextInputInGrid.vue";
import SelectItem from "@/components/SelectItem.vue";
import NumericLabel from "@/components/NumericLabel.vue";
import TutorialWizard from "@/components/TutorialWizard.vue";

import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";

import { Bar, Line } from "vue-chartjs"

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import DisableAutocomplete from "vue-disable-autocomplete";

import SharedPopup from "@/components/SharedPopup.vue";
import eventBus from './eventBus';

//tooltip
import Popper from "vue3-popper";

import mitt from 'mitt';
import {
  Chart as ChartJS,
  Title,
  Tooltip as TooltipChartJS,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement
} from "chart.js"

ChartJS.register(Title, TooltipChartJS, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement);

const options = {
  // default options here
  position: "bottom-center",
  timeout: 2000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: true,
  closeButton: "button",
  icon: true,
  rtl: false,
  maxToasts: 1
};
const emitter = mitt();
const app = 
  createApp({
    extends: App,
    async beforeCreate() {
      await Promise.all([
        store.dispatch("appInitComplete", false),
        store.dispatch("initialise"),
        stripeService.getStripeConfig().catch(), 
        authService.refreshToken().catch(() => {
          store.dispatch("clearLogin");
          store.dispatch("clearImpersonateLogin");
          router.push("/login");
        }),
        $http.get("/api/GlobalSettings/AppSetting/Recaptcha.SiteKey").then((response) => {
          store.commit("setRecaptchaSiteKey", response.data);
        })
      ]);
      store.dispatch("appInitComplete", true);
    },
    components: {
      Validator,
      ValidatorInstaller
    }
  }).use(router)
    .use(store);

app.config.globalProperties.$eventBus = eventBus;         
app.config.globalProperties.emitter = emitter;
app.use(Toast, options);

app.use(DisableAutocomplete);

axios.defaults.baseURL = process.env.VUE_APP_API_URL
axios.defaults.headers.post["Content-Type"] = "application/json"

axios.interceptors.request.use(config => {
  let token = authHeader(store);
  if (token !== localStorage.getItem("jwt_token")) {
    store.dispatch("initialise");
    token = authHeader(store);
  }
  
  if (token.Authorization && config.headers) {
    config.headers.Authorization = token.Authorization;
  }
  
  return config;
});
axios.interceptors.response.use(
  resp => resp, //plain passthrough for successful responses
  async (err) => {
    if (err.response) {
      const url = err.request.responseURL || err.request.baseURL;

      const isRefreshError = url.toLowerCase().indexOf("/api/account/refreshtoken") > 0; // Error occured in refresh command
      const isLogOutError = url.toLowerCase().indexOf("/api/account/logoff") > 0; // Error occured in logout command

      const isOutOfDate = err.response.status == 428;
      const isNotSubscribed = err.response.status == 402;

      if ((err.response.status === 401 || isOutOfDate || isNotSubscribed) && !err.config._retry && !isLogOutError) {
        err.config._retry = true;
        try {
          if (isRefreshError) {
            authService.logout().then(() => {
              router.push("/login");
            });
            return Promise.reject(new Error("Could not refresh login token"));
          }
          const rs = await authService.refreshToken();
          if (rs && rs.data && rs.data && rs.data.authenticated) {
            if ((!rs.data.businessID && isOutOfDate) || rs.data.subscriptionStatus.locked || isNotSubscribed) {
              router.push("/dashboard");
            }
            else {
              return axios(err.config);
            }
          }
          else {
            store.commit("setIsLoading", false);
            router.push("/login");
            return Promise.reject(err);
          }
        }
        catch (_err: any) {
          if (_err.response && _err.response.data) {
            return Promise.reject(_err.response.data);
          }
        }

        return Promise.reject(err);
      }
      return Promise.reject(err);
    }
    return Promise.reject(err);
  }
);

app.config.globalProperties.$axios = axios;

app.use(Validator)
app.use(ValidatorInstaller)

//Axios
app.component("ax-dialog-header", DialogHeader);
app.component("version", Version);
app.component("select-item", SelectItem);
app.component("ax-result-column-with-arrow", ResultColumnWithArrow);
app.component("ax-input-in-grid", InputInGrid);
app.component("ax-text-input-in-grid", TextInputInGrid);
app.component("ax-numeric-label", NumericLabel);
app.component("tutorial-wizard", TutorialWizard);

// Global Kendo Component Registration
app.component("k-dropdown-list", DropDownList);
app.component("k-grid", Grid);
app.component("k-window", Window);
app.component("k-form", Form);
app.component("k-button", Button);
app.component("k-button-group", ButtonGroup);
app.component("k-form", Form);
app.component("k-field", Field);
app.component("k-error", Error);
app.component("k-hint", Hint);
app.component("k-label", Label);
app.component("k-input", Input);
app.component("k-masked-textbox", MaskedTextBox);
app.component("k-numeric-textbox", NumericTextBox);
app.component("k-checkbox", Checkbox);
app.component("k-radio-button", RadioButton);
app.component("k-textarea", TextArea);
//app.component("k-datepicker", DatePicker);
app.component("k-notification", Notification);
app.component("k-tooltip", Tooltip);
app.component("k-dialog", Dialog);
app.component("k-panelbar", PanelBar);
app.component("k-card", Card);
app.component("k-card-header", CardHeader);
app.component("k-card-body", CardBody);
app.component("k-card-title", CardTitle);
app.component("k-card-subtitle", CardSubtitle);
app.component("k-card-image", CardImage);
app.component("k-card-actions", CardActions);
app.component("k-upload", Upload);
app.component("k-expand", Expand);
app.component("k-switch", Switch);
app.component("k-radio", RadioButton);
app.component("k-dropdownbutton", DropDownButton);

app.component("cjs-bar-chart", Bar);
app.component("cjs-line-chart", Line);

app.component("ax-shared-popup", SharedPopup);

//tooltip
app.component("Popper", Popper);

app.mount("#app");